import { cn } from '@lib/utils';
import { Loader2, type LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';


export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-[13px] gap-1.5',
  md: 'h-11 px-5 text-[15px] gap-2',
  lg: 'h-12 px-6 text-[16px] gap-2',
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-primary text-white shadow-btn-green hover:shadow-glow-green hover:scale-[1.02] active:scale-[0.98]',
  secondary:
    'bg-apple-gray-100 text-apple-label hover:bg-apple-gray-300 active:bg-apple-gray-400/60',
  ghost:
    'bg-transparent text-link hover:bg-[#EFF6FF] active:bg-[#DBEAFE]',
  // Never red, rule 3.
  danger:
    'bg-gradient-danger text-white shadow-[0_4px_16px_rgba(244,63,94,0.32)] hover:scale-[1.02] active:scale-[0.98]',
  icon: 'bg-apple-gray-100 text-apple-label-secondary hover:bg-apple-gray-300 rounded-md h-10 w-10 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isIcon = variant === 'icon';
  const iconSize = ICON_SIZE[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'relative inline-flex items-center justify-center font-medium rounded-md',
        'transition-all duration-200 ease-apple select-none',
        'disabled:opacity-50 disabled:pointer-events-none',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-link/25 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        !isIcon && SIZE[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 size={iconSize} className="animate-spin" strokeWidth={2.5} />
        </span>
      )}
      <span
        className={cn(
          'inline-flex items-center justify-center gap-2',
          loading && 'opacity-0',
        )}
      >
        {LeftIcon ? <LeftIcon size={iconSize} strokeWidth={1.5} /> : null}
        {children}
        {RightIcon ? <RightIcon size={iconSize} strokeWidth={1.5} /> : null}
      </span>
    </button>
  );
});
